import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Eye, Sparkles, Check } from 'lucide-react';
import BaseballService from '../../../components/dashboards/services/baseballService';
import { useAddToChat } from '../../../components/chat/chatContext';

const MobileGameCardWithPrediction = memo(({
  game,
  onViewOdds,
  isSelected,
  isFrozen = false,
  onToggleSelection,
  prediction,
  showPredictionButton = true
}) => {
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `Analyze this baseball prediction:

${teams?.away?.name} @ ${teams?.home?.name}

Prediction: ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% confidence)` : ''}
Confidence Level: ${prediction.confidence}
${prediction.reason ? `Reasoning: ${prediction.reason}` : ''}

Can you provide a deeper analysis of this matchup?`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!game) return null;

  const { teams, scores, date, time, status } = game;
  const statusShort = status?.short;
  const isLive = statusShort?.startsWith('IN') || statusShort === 'LIVE' || statusShort === 'IN_PLAY';
  const isFinished = statusShort === 'FT' || statusShort === 'AOT' || statusShort === 'FINISHED';
  const isUpcoming = statusShort === 'NS';
  
  // Get display text for status badge
  const getStatusDisplay = () => {
    if (isLive) {
      // For baseball, show inning if available
      if (status?.long?.includes('Inning')) {
        const inning = status.long.match(/(\d+)\w*\s+Inning/)?.[1];
        return inning ? `LIVE ${inning}` : 'LIVE';
      }
      return 'LIVE';
    }
    if (isFinished) return 'FT';
    if (statusShort === 'POST') return 'PPD';
    return null;
  };
  
  const statusDisplay = getStatusDisplay();
  
  // Format the date and time for display in ET
  const formatGameTime = () => {
    // TIMEZONE FIX: Always convert to ET (America/New_York) for consistent display
    // BaseballService.formatTime handles the conversion properly
    if (date) {
      try {
        // If we have separate date and time fields, combine them
        if (time && !date.includes('T')) {
          const dateTimeString = `${date}T${time}`;
          const gameDate = new Date(dateTimeString);
          if (!isNaN(gameDate.getTime())) {
            // BaseballService.formatTime converts to ET timezone
            return `${BaseballService.formatTime(dateTimeString)} ET`;
          }
        } else {
          // Date already includes time component
          const gameDate = new Date(date);
          if (!isNaN(gameDate.getTime())) {
            return `${BaseballService.formatTime(date)} ET`;
          }
        }
      } catch (e) {
        // If date parsing fails, fall through to fallbacks
      }
    }

    // Fallback: Time-only display (cannot convert timezone without date)
    // Assume times are already in ET if no date is provided
    if (time) {
      if (time.includes('AM') || time.includes('PM')) {
        return time.includes('ET') ? time : `${time} ET`;
      }
      if (time.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm} ET`;
      }
      return `${time} ET`;
    }

    return 'TBD';
  };

  return (
    <motion.div
      whileTap={{ scale: 0.96 }}
      className={`relative bg-white/[0.03] rounded-xl p-2.5 active:scale-[0.96] active:bg-cyan-500/10 transition-all border ${
        isSelected 
          ? 'border-emerald-500/30 bg-emerald-500/20' 
          : 'border-white/[0.08]'
      }`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Status Badge - Top Right */}
      {statusDisplay && (
        <div className="absolute top-2 right-2 z-10">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
            isLive ? 'bg-red-500' : statusShort === 'POST' ? 'bg-yellow-500/20' : 'bg-white/20'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
            <span className="text-[10px] font-bold text-white">
              {statusDisplay}
            </span>
          </div>
        </div>
      )}
      
      {/* League Label */}
      <div className="text-[9px] text-white/65 font-semibold uppercase tracking-wider mb-1.5 truncate pr-16">
        {game.league?.name || 'MLB'}
      </div>
      
      {/* Teams and Scores */}
      <div className="space-y-2">
        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {teams?.away?.logo && (
              <img 
                src={teams.away.logo} 
                alt={teams.away.name}
                className="w-5 h-5 object-contain flex-shrink-0"
                loading="lazy"
              />
            )}
            <span className="text-[11px] font-medium text-white truncate">
              {teams?.away?.name || 'Away Team'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-2 tabular-nums">
            {isLive || isFinished ? (scores?.away?.total ?? 0) : '-'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {teams?.home?.logo && (
              <img 
                src={teams.home.logo} 
                alt={teams.home.name}
                className="w-5 h-5 object-contain flex-shrink-0"
                loading="lazy"
              />
            )}
            <span className="text-[11px] font-medium text-white truncate">
              {teams?.home?.name || 'Home Team'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-2 tabular-nums">
            {isLive || isFinished ? (scores?.home?.total ?? 0) : '-'}
          </span>
        </div>
      </div>
      
      {/* Game Time - only for upcoming games */}
      {!isFinished && !isLive && (
        <div className="mt-2 pt-1.5 border-t border-white/10">
          <div className="flex items-center justify-center gap-1 text-white/50">
            <Clock size={9} />
            <span className="text-[9px] font-medium">
              {formatGameTime()}
            </span>
          </div>
        </div>
      )}

      {/* Prediction Display - if available */}
      {prediction && (
        <div className="mt-2 p-2 bg-violet-500/10 rounded-lg border border-violet-500/20">
          <div className="text-sm text-white">
            {prediction.winner && prediction.confidence ? (
              <div className="flex items-center gap-2">
                <span className="font-bold flex-1">
                  {prediction.winner}
                  {prediction.confidenceNumeric && (
                    <span className="text-white/80 font-normal ml-1">({prediction.confidenceNumeric}%)</span>
                  )}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                  prediction.confidence === 'HIGH' ? 'bg-green-500/20 text-green-300' :
                  prediction.confidence === 'MED' ? 'bg-yellow-500/20 text-yellow-300' :
                  'bg-red-500/20 text-red-300'
                }`}>
                  {prediction.confidence}
                </span>
              </div>
            ) : (
              <span className="text-xs">{prediction.full || prediction}</span>
            )}
          </div>
          {prediction.reason && (
            <p className="text-xs text-white/60 mt-1">
              {prediction.reason}
            </p>
          )}
          <button
            onClick={handleAnalyzeInChat}
            className="text-xs text-violet-400 hover:text-violet-300 hover:underline mt-2 inline-block transition-colors cursor-pointer bg-transparent border-none p-0"
          >
            Analyze in Chat →
          </button>
          {/* Show cached indicator if prediction is cached */}
          {prediction.cached && (
            <div className="text-xs text-yellow-400 mt-1 font-semibold">
              Cached • {prediction.cooldownRemaining}m remaining
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 mt-2">
        {/* Prediction Button */}
        {showPredictionButton && (isUpcoming || isLive) && onToggleSelection && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!isFrozen) {
                onToggleSelection(game);
              }
            }}
            disabled={isFrozen}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-medium tracking-[0.12em] transition-all uppercase ${
              isFrozen
                ? 'border border-white/10 text-white/30 cursor-not-allowed'
                : isSelected
                ? 'bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 text-white shadow-lg'
                : isLive
                  ? 'border border-emerald-500/30 text-emerald-400/80 hover:text-emerald-400/90 hover:bg-emerald-500/[0.02]'
                  : 'border border-violet-500/30 text-violet-400/70 hover:text-violet-400/90 hover:bg-white/[0.02]'
            }`}
          >
            {isFrozen ? (
              'Cooldown'
            ) : isSelected ? (
              'Selected'
            ) : isLive ? (
              'Predict Live'
            ) : (
              'Predict'
            )}
          </button>
        )}

        {/* Odds Button */}
        {onViewOdds && (
          <button
            onClick={() => onViewOdds(game)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 border border-white/10 text-[10px] font-medium tracking-[0.12em] text-white/70 hover:text-white/90 hover:border-white/20 hover:bg-white/[0.02] transition-all uppercase"
          >
            Odds
          </button>
        )}
      </div>
    </motion.div>
  );
});

MobileGameCardWithPrediction.displayName = 'MobileGameCardWithPrediction';

export default MobileGameCardWithPrediction;